import { GetString } from 'fluent-react';
import sortBy from 'lodash-es/sortBy';
import React, { RefObject } from 'react';
import {
  CountryProfile,
  GlobalDatum,
  ProductClass,
  ProductLevel,
  TreeMapInput,
  TreeMapProduct,
  TreeMapType,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { generateStringProductId } from '../../graphQL/Utils';
import { formatPercentage } from '../../numberFormatters';
import { SlideElementProps } from '../../presentationManager/PresentationManager';
import {
  IndicatorsContainerBase,
} from '../Grid';
import Indicator, {
  Status,
  TextWeight,
  Type,
} from '../topPageIndicator/Indicator';

const query = gql`
  query GrowthGlobalMarketShareTopOfPageIndicators(
    $id: ID!,
    $facet: TreeMapType!,
    $productClass: ProductClass,
    $year: Int,
    $productLevel: ProductLevel,
    $locationLevel: LocationLevel,
    $location: ID,
    $product: ID,
    $partner: ID,
  ) {
    globalData: globalDatum {
      globalExportValue
    }

    location: countryProfile(location: $id) {
      exportValue
    }

    treeMap(
      facet: $facet,
      productClass: $productClass,
      year: $year,
      productLevel: $productLevel,
      locationLevel: $locationLevel,
      location: $location,
      product: $product,
      partner: $partner,
    ) {
      ... on TreeMapProduct {
        product {
          id
          longName
        }
        globalMarketShare
      }
    }
  }
`;

interface SuccessResponse {
  globalData: {
    globalExportValue: GlobalDatum['globalExportValue'];
  };
  location: null | {
    exportValue: CountryProfile['exportValue'];
  };
  treeMap: null | Array<{
    product: {
      id: TreeMapProduct['product']['id']
      longName: TreeMapProduct['product']['longName'],
    };
    globalMarketShare: TreeMapProduct['globalMarketShare'];
  }>;
}

interface Variables extends TreeMapInput {
  id: string;
}

interface Props extends SlideElementProps {
  countryId: string;
  getFluentString: GetString;
  zIndex: number;
  year: number;
}

const TopOfPageIndicators = React.forwardRef((props: Props, rootElRef: RefObject<HTMLDivElement>) => {

  const {getFluentString, zIndex, year} = props;

  const variables: Variables = {
    id: props.countryId,
    facet: TreeMapType.CPY_C,
    productClass: ProductClass.HS,
    year,
    productLevel: ProductLevel.section,
    locationLevel: null,
    location: props.countryId,
    product: null,
    partner: null,
  };

  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables});
  const largestMarketShareLabel = getFluentString('largest-market-share-label');
  const fastestGrowingMarketShareLabel = getFluentString('share-of-global-trade');

  let out: React.ReactElement<any> | null;
  if (loading === true) {
    out = (
      <>
        <Indicator
          type={Type.Text}
          status={Status.Loading}
          label={largestMarketShareLabel}
        />
        <Indicator
          type={Type.Text}
          status={Status.Loading}
          label={fastestGrowingMarketShareLabel}
        />
      </>
    );
  } else if (error !== undefined) {
    console.error(error);
    out = (
      <>
        <Indicator
          type={Type.Text}
          status={Status.Failure}
          label={largestMarketShareLabel}
        />
        <Indicator
          type={Type.Text}
          status={Status.Failure}
          label={fastestGrowingMarketShareLabel}
        />
      </>
    );
  } else if (data !== undefined) {
    const {location, treeMap: marketSectors, globalData: { globalExportValue }} = data;
    if (location === null) {
      out = (
        <>
          <Indicator
            type={Type.Text}
            status={Status.Failure}
            label={largestMarketShareLabel}
          />
          <Indicator
            type={Type.Text}
            status={Status.Failure}
            label={fastestGrowingMarketShareLabel}
          />
        </>
      );
    } else {
      const { exportValue } = location;
      const exportPercent = exportValue === null ? 'N/A'
        : formatPercentage((exportValue / globalExportValue)).toString();
      let largestMarketShareSectorText: string;
      if (marketSectors === null) {
        largestMarketShareSectorText = 'N/A';
      } else {
        // Remove "other" sector:
        const otherCategoryID = generateStringProductId({productClass: ProductClass.HS, id: 10});
        const filteredSectors = marketSectors.filter(elem => elem.product.id !== otherCategoryID);
        const largestMarketShareSector = sortBy( filteredSectors, [ ({globalMarketShare}) => {
          if (globalMarketShare !== null) {
            return globalMarketShare;
          }
        }]).pop();

        largestMarketShareSectorText =
          largestMarketShareSector !== undefined ? largestMarketShareSector.product.longName : 'N/A';
      }

      const largestMarketShareIndicator = (
        <Indicator
          status={Status.Success}
          type={Type.Text}
          weight={TextWeight.Light}
          label={largestMarketShareLabel}
          text={largestMarketShareSectorText}
        />
      );
      const fastestGrowingMarketShareIndicator = (
        <Indicator
          status={Status.Success}
          type={Type.Text}
          weight={TextWeight.Light}
          label={fastestGrowingMarketShareLabel}
          text={exportPercent}
        />
      );
      out = (
        <>
          {largestMarketShareIndicator}
          {fastestGrowingMarketShareIndicator}
        </>
      );
    }

  } else {
    out = null;
  }
  const style: React.CSSProperties = {
    zIndex,
  };
  return (
    <IndicatorsContainerBase style={style} ref={rootElRef}>
      {out}
    </IndicatorsContainerBase>
  );
});

export default TopOfPageIndicators;
